import { cn } from "@/lib/utils";
import type { DynamicAction } from "@/types/dynamic-detail";
import { AlertCircle, Info, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Actions2 } from "./actions-2";

type AlertItem = {
    label: string;
    href?: string | null;
};

type Alert = {
    title: string | null;
    content: string | null;
    type: "warning" | "error" | "info";
    href: string | null;
    /** A `DynamicAction` of the record, offered from the alert when the record serves it. */
    action_name?: string | null;
    items?: AlertItem[];
    details?: string | null;
};

export type AlertLayoutProps = {
    alerts: Alert[];
    /** The record's actions (`detail-data`): an alert's `action_name` opens one of them. */
    actions?: DynamicAction[];
    /** The record, for the action modal. */
    objectId?: string | number;
    /** `/crm/objects/<object>/`, the endpoint the actions run on. */
    endpointUrl?: string;
    onActionSuccess?: () => void;
    /** Inside a dialog or another panel: the alerts without the panel around them. */
    embedded?: boolean;
    /** Small type and padding, for a side column. */
    compact?: boolean;
};

function AlertLink({ href, children, compact }: { href: string; children: ReactNode; compact?: boolean }) {
    const className = cn(compact ? "text-xs" : "text-sm", "text-blue-700 hover:text-blue-900 hover:underline");
    if (/^https?:\/\//.test(href)) {
        return (
            <a className={className} href={href} target="_blank" rel="noreferrer">
                {children}
            </a>
        );
    }
    return (
        <Link className={className} to={href}>
            {children}
        </Link>
    );
}

export function AlertLayout(props: AlertLayoutProps) {
    if (props.alerts.length === 0) return null;

    return (
        <div className={cn("flex flex-col items-start space-y-2", props.embedded ? (props.compact ? "" : "pt-2") : "mt-2 rounded-md bg-white p-4 shadow-md")}>
            {props.alerts.map((alert, idx) => {
                const action = alert.action_name ? props.actions?.find(candidate => candidate.action_name === alert.action_name) : undefined;
                return (
                    <div
                        key={`alert-${idx}`}
                        className={cn("flex w-full items-start gap-2 rounded-md text-gray-900", props.compact ? "p-2" : "p-3", alert.type === "warning" ? "bg-amber-50" : alert.type === "error" ? "bg-red-50" : "bg-blue-50")}
                    >
                        {alert.type === "warning" ? (
                            <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-800" />
                        ) : alert.type === "error" ? (
                            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-800" />
                        ) : (
                            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-800" />
                        )}
                        <div className="min-w-0 flex-1 space-y-1">
                            {alert.title && <h3 className={cn(props.compact ? "text-xs" : "text-sm", "font-semibold")}>{alert.title}</h3>}
                            {alert.content && <p className={cn(props.compact ? "text-xs" : "text-sm", "whitespace-pre-line")}>{alert.content}</p>}
                            {alert.items && alert.items.length > 0 && (
                                <ul className="list-disc pl-5 text-sm">
                                    {alert.items.map((item, itemIdx) => (
                                        <li key={`alert-${idx}-item-${itemIdx}`}>{item.href ? <AlertLink href={item.href}>{item.label}</AlertLink> : item.label}</li>
                                    ))}
                                </ul>
                            )}
                            {alert.details && (
                                <details className="text-xs">
                                    <summary className="cursor-pointer underline">Plus de détails...</summary>
                                    <pre className="mt-1 max-h-[50vh] overflow-y-auto whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono">{alert.details}</pre>
                                </details>
                            )}
                            {alert.href && (
                                <AlertLink href={alert.href} compact={props.compact}>
                                    View details...
                                </AlertLink>
                            )}
                            {action && props.endpointUrl && props.objectId != null && (
                                <div className="pt-1">
                                    <Actions2
                                        actions={[action]}
                                        selectedItemIds={[props.objectId.toString()]}
                                        selectAll={false}
                                        selectAllCount={0}
                                        endpointUrl={props.endpointUrl}
                                        action_scope="detail"
                                        layout="standalone"
                                        onActionSuccess={props.onActionSuccess}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
